import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonGroupItem,
  CharacterCount,
  Hint,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@boozallen-uip/uswds-react-core";
import { IndeterminateSpinner } from "@boozallen-uip/uswds-react-loading";
import TypeaheadCombobox from "../../components/TypeaheadCombobox.component";
import "./RequestAcrModal.component.css";
import useCurrentOrg from "../../hooks/useCurrentOrg.hook";
import useRequestAcr from "../../hooks/useRequestAcr.hook";
import { UserContext } from "../UserContext";
import { VendorContext } from "../Vendor/VendorContext";

const ACR_REQUEST_RECIPIENT = "acr.repository@gsa.gov";
const NAME_MAX = 200;
const COMMENTS_MAX = 5000;
const MAILTO_COMMENT_LIMIT = 500;
const MODAL_ID = "request-acr-modal";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const SERVER_FIELD_KEYS = {
  productname: "productName",
  vendorname: "vendorName",
  comments: "comments",
};

function validateFields({ productName, vendorName, comments }) {
  const errors = {};
  const product = productName.trim();
  const vendor = vendorName.trim();
  if (!product) {
    errors.productName = "Enter the product name.";
  } else if (product.length > NAME_MAX) {
    errors.productName = "Product name must be 200 characters or fewer.";
  }
  if (!vendor) {
    errors.vendorName = "Enter the vendor name.";
  } else if (vendor.length > NAME_MAX) {
    errors.vendorName = "Vendor name must be 200 characters or fewer.";
  }
  if (comments.length > COMMENTS_MAX) {
    errors.comments = "Comments must be 5,000 characters or fewer.";
  }
  return errors;
}

function mapServerFieldErrors(fieldErrors) {
  const mapped = {};
  Object.entries(fieldErrors || {}).forEach(([key, message]) => {
    const field = SERVER_FIELD_KEYS[String(key).toLowerCase()];
    if (field && message && !mapped[field]) {
      mapped[field] = message;
    }
  });
  return mapped;
}

function buildMailto({ productName, vendorName, comments, buyerName, buyerOrganization }) {
  const subject = `ACR Request: ${productName.trim()} \u2013 ${vendorName.trim()}`;
  const trimmedComments = comments.trim();
  const shortComments = trimmedComments.length > MAILTO_COMMENT_LIMIT
    ? `${trimmedComments.slice(0, MAILTO_COMMENT_LIMIT)}…`
    : trimmedComments;
  const body = [
    `Buyer name: ${buyerName || ""}`,
    `Buyer organization: ${buyerOrganization || ""}`,
    `Product name: ${productName.trim()}`,
    `Vendor name: ${vendorName.trim()}`,
    `Comments: ${shortComments || "(none)"}`,
  ].join("\n");
  return `mailto:${ACR_REQUEST_RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function joinIds(ids) {
  const value = ids.filter(Boolean).join(" ");
  return value || undefined;
}

function RequiredMark() {
  return (
    <abbr title="required" className="usa-hint usa-hint--required">*</abbr>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  // Matches the inline field errors used across the app (Product/ACR-editor forms):
  // small red text below the field. A span (not a div) keeps it valid inside the label.
  return (
    <span id={id} className="block mt-1 text-sm text-red" role="alert">
      {message}
    </span>
  );
}

FieldError.propTypes = {
  id: PropTypes.string.isRequired,
  message: PropTypes.string,
};

FieldError.defaultProps = {
  message: "",
};

export default function RequestAcrModal({ isOpen, onClose }) {
  const { user, request } = useContext(UserContext);
  const { vendors } = useContext(VendorContext);
  const currentOrg = useCurrentOrg();
  const { requestAcr } = useRequestAcr();

  const [productName, setProductName] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [comments, setComments] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formMessage, setFormMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [statusBeforeConfirm, setStatusBeforeConfirm] = useState("idle");
  const discardDialogRef = useRef(null);
  const returnFocusAfterConfirmRef = useRef(false);

  // The dialog stays mounted so USWDS can run its close path. Clear the draft when it
  // closes; resetting on open would paint the previous request before this effect runs.
  useEffect(() => {
    if (isOpen) return;
    setProductName("");
    setVendorName("");
    setComments("");
    setFieldErrors({});
    setFormMessage("");
    setStatus("idle");
    setStatusBeforeConfirm("idle");
  }, [isOpen]);

  // Cancel unmounts when this view replaces the form. Focus the alertdialog so
  // the warning is announced, and return to the product field if the user stays.
  useEffect(() => {
    if (!isOpen || status !== "confirm") return;
    discardDialogRef.current?.focus();
  }, [isOpen, status]);

  useEffect(() => {
    if (!isOpen || status === "confirm" || status === "success") return;
    if (!returnFocusAfterConfirmRef.current) return;
    returnFocusAfterConfirmRef.current = false;
    document.getElementById("request-acr-product")?.focus();
  }, [isOpen, status]);

  // Disabling the focused control moves focus to the page. Park it on the dialog
  // shell until a control is available again.
  useEffect(() => {
    if (!isOpen || status !== "submitting") return;
    document.getElementById(MODAL_ID)?.querySelector(".usa-modal")?.focus();
  }, [isOpen, status]);

  // Keep Tab/Shift+Tab cycling within the dialog. The USWDS trap captures the
  // focusable set once at open, which goes stale as this modal swaps between its
  // idle/confirm/success states; recompute against the live DOM on every Tab.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const modalEl = document.getElementById(MODAL_ID);
      if (!modalEl) return;

      const focusable = Array.from(modalEl.querySelectorAll(FOCUSABLE_SELECTOR));
      // Submitting disables every control. Keep Tab inside the dialog anyway.
      if (focusable.length === 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        modalEl.querySelector(".usa-modal")?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const currentIndex = focusable.indexOf(active);

      // Own Tab handling outright so the stale USWDS trap cannot also act.
      event.preventDefault();
      event.stopImmediatePropagation();

      if (currentIndex === -1) {
        (event.shiftKey ? last : first).focus();
        return;
      }

      const lastIndex = focusable.length - 1;
      let nextIndex;
      if (event.shiftKey) {
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      } else {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      }
      focusable[nextIndex].focus();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);

  // The USWDS React modal closes on an overlay click by synthesizing Escape.
  // forceAction omits [data-close-modal], so that path still strips `is-visible`
  // while `isOpen` stays true and the request cannot be opened again. Stop the
  // overlay click before it reaches the modal's onClick.
  useEffect(() => {
    if (!isOpen) return undefined;

    const blockOverlayDismiss = (event) => {
      const wrapper = document.getElementById(MODAL_ID);
      const overlay = wrapper?.querySelector(".usa-modal-overlay");
      if (!overlay || event.target !== overlay) return;
      event.stopPropagation();
    };

    document.addEventListener("click", blockOverlayDismiss, true);
    return () => document.removeEventListener("click", blockOverlayDismiss, true);
  }, [isOpen]);

  const vendorOptions = useMemo(() => {
    const list = Array.isArray(vendors) ? vendors : [];
    const query = vendorName.trim().toLowerCase();
    if (!query) return list;
    return list.filter((vendor) => String(vendor?.name || "").toLowerCase().includes(query));
  }, [vendorName, vendors]);

  const isDirty = Boolean(productName || vendorName || comments);
  const isSubmitting = status === "submitting";
  // AppUser carries email and org ids only. The person's name lives on the access request.
  const buyerName = request?.name || "";
  const buyerOrganization = currentOrg?.name || "";

  // Clear a field's error as the user edits it, matching the add/edit product forms
  // (ProductInformationSection calls clearFieldErrors on change).
  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submit = async () => {
    const errors = validateFields({ productName, vendorName, comments });
    setFieldErrors(errors);
    setFormMessage("");
    if (Object.keys(errors).length > 0) {
      setStatus("idle");
      return;
    }

    setStatus("submitting");
    const result = await requestAcr({
      productName: productName.trim(),
      vendorName: vendorName.trim(),
      comments: comments.trim() || null,
      buyerName,
      buyerOrganization,
      buyerEmail: user?.email || "",
    });

    if (result.ok) {
      setStatus("success");
      return;
    }
    if (result.type === "validation") {
      const mapped = mapServerFieldErrors(result.fieldErrors);
      setFieldErrors(mapped);
      if (Object.keys(mapped).length === 0) {
        const firstMessage = Object.values(result.fieldErrors || {})[0];
        setFormMessage(firstMessage || "Check the product name, vendor name, and comments, then try again.");
      }
      setStatus("idle");
      return;
    }
    setStatus("error");
  };

  const requestCancel = () => {
    if (isSubmitting) return;
    if (!isDirty || status === "success") {
      onClose();
      return;
    }
    setStatusBeforeConfirm(status === "confirm" ? "idle" : status);
    setStatus("confirm");
  };

  const mailtoHref = buildMailto({
    productName,
    vendorName,
    comments,
    buyerName,
    buyerOrganization,
  });

  return (
    <Modal
      id="request-acr-modal"
      className="request-acr-modal"
      labelledBy="request-acr-heading"
      describedBy="request-acr-body"
      isOpen={isOpen}
      large
      forceAction
      onStateChanged={() => {
        // USWDS only applies `isOpen` when this callback exists. Overlay clicks are
        // blocked above; closing stays on an explicit button.
      }}
    >
      <ModalHeading id="request-acr-heading">Request an ACR</ModalHeading>
      <ModalBody id="request-acr-body">
        {status === "success" ? (
          <div role="status" aria-live="polite">
            <Alert type="success">
              Your ACR request was successfully submitted to acr.repository@gsa.gov.
            </Alert>
          </div>
        ) : null}

        {status === "confirm" ? (
          <div
            ref={discardDialogRef}
            role="alertdialog"
            tabIndex={-1}
            aria-labelledby="request-acr-discard-heading"
            aria-describedby="request-acr-discard-body"
          >
            <h3 id="request-acr-discard-heading" className="margin-top-0">Discard this request?</h3>
            <p id="request-acr-discard-body">
              If you close now, your ACR request won&apos;t be sent and your entries will be lost.
            </p>
            <ButtonGroup>
              <ButtonGroupItem>
                <Button
                  type="button"
                  onClick={() => {
                    returnFocusAfterConfirmRef.current = true;
                    setStatus(statusBeforeConfirm);
                  }}
                >
                  Keep editing
                </Button>
              </ButtonGroupItem>
              <ButtonGroupItem>
                <Button type="button" outline onClick={onClose}>Discard request</Button>
              </ButtonGroupItem>
            </ButtonGroup>
          </div>
        ) : null}

        {status !== "success" && status !== "confirm" ? (
          <form
            id="request-acr-form"
            className="usa-form usa-form--large"
            onSubmit={(event) => {
              event.preventDefault();
              if (!isSubmitting) submit();
            }}
          >
            <p id="request-acr-intro" className="margin-top-0 margin-bottom-3">
              Tell us which product you need an ACR for. We&apos;ll email your request to the ACR Repository team.
            </p>

            {status === "submitting" ? (
              <div role="status" aria-live="polite" className="margin-bottom-2">
                <p className="margin-top-0">Sending your request…</p>
                <IndeterminateSpinner type="circle" color="blue" />
              </div>
            ) : null}

            {status === "error" ? (
              <Alert type="error" heading="We couldn't send your request">
                Something went wrong while sending your ACR request. Your entries are saved below — you can try again, or email your request directly to acr.repository@gsa.gov.
              </Alert>
            ) : null}

            {formMessage ? (
              <div role="alert" className="usa-error-message margin-bottom-2">{formMessage}</div>
            ) : null}

            <div className="usa-form-group">
              <label className="usa-label" htmlFor="request-acr-product">
                <span id="request-acr-product-label">Product name</span>
                {" "}
                <RequiredMark />
                <input
                  id="request-acr-product"
                  className="usa-input"
                  type="text"
                  value={productName}
                  maxLength={NAME_MAX}
                  autoComplete="off"
                  disabled={isSubmitting}
                  aria-labelledby="request-acr-product-label"
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.productName) || undefined}
                  aria-describedby={joinIds([
                    fieldErrors.productName ? "request-acr-product-error" : null,
                  ])}
                  onChange={(event) => {
                    clearFieldError("productName");
                    setProductName(event.target.value);
                  }}
                />
                <FieldError id="request-acr-product-error" message={fieldErrors.productName} />
              </label>
            </div>

            <div className="usa-form-group">
              <label className="usa-label" htmlFor="request-acr-vendor">
                <span id="request-acr-vendor-label">Vendor name</span>
                {" "}
                <RequiredMark />
                <Hint id="request-acr-vendor-hint" className="display-block">
                  Start typing to search existing vendors, or enter a new one.
                </Hint>
                <TypeaheadCombobox
                  id="request-acr-vendor"
                  value={vendorName}
                  onChange={(next) => {
                    clearFieldError("vendorName");
                    setVendorName(next);
                  }}
                  options={vendorOptions}
                  getOptionKey={(vendor) => vendor.id || vendor.name}
                  renderOption={(vendor) => vendor.name}
                  onSelect={(vendor) => {
                    clearFieldError("vendorName");
                    setVendorName(vendor.name || "");
                  }}
                  openOnFocus
                  allowFreeText
                  onCommitFreeText={(typed) => {
                    clearFieldError("vendorName");
                    setVendorName(typed);
                  }}
                  disabled={isSubmitting}
                  placeholder="Type a vendor name"
                  noResultsLabel="No matching vendors"
                  maxLength={NAME_MAX}
                  aria-labelledby="request-acr-vendor-label"
                  aria-required
                  aria-invalid={Boolean(fieldErrors.vendorName) || undefined}
                  aria-describedby={joinIds([
                    "request-acr-vendor-hint",
                    fieldErrors.vendorName ? "request-acr-vendor-error" : null,
                  ])}
                />
                <FieldError id="request-acr-vendor-error" message={fieldErrors.vendorName} />
              </label>
            </div>

            <CharacterCount target="request-acr-comments">
              <div className="usa-form-group">
                <label className="usa-label" htmlFor="request-acr-comments">
                  <span id="request-acr-comments-label">Comments (optional)</span>
                  <Hint id="request-acr-comments-hint" className="display-block">
                    Share anything that will help us locate the right ACR (version, product URL, etc.).
                  </Hint>
                  <textarea
                    id="request-acr-comments"
                    className="usa-textarea"
                    rows={4}
                    value={comments}
                    maxLength={COMMENTS_MAX}
                    autoComplete="off"
                    disabled={isSubmitting}
                    aria-labelledby="request-acr-comments-label"
                    aria-invalid={Boolean(fieldErrors.comments) || undefined}
                    aria-describedby={joinIds([
                      "request-acr-comments-hint",
                      fieldErrors.comments ? "request-acr-comments-error" : null,
                    ])}
                    onChange={(event) => {
                      clearFieldError("comments");
                      setComments(event.target.value);
                    }}
                  />
                  <FieldError id="request-acr-comments-error" message={fieldErrors.comments} />
                </label>
              </div>
            </CharacterCount>
          </form>
        ) : null}
      </ModalBody>
      {status !== "confirm" ? (
        <ModalFooter>
          <ButtonGroup>
            {status === "success" ? (
              <ButtonGroupItem>
                <Button type="button" onClick={onClose}>Return to ACRs</Button>
              </ButtonGroupItem>
            ) : (
              <>
                <ButtonGroupItem>
                  <Button type="submit" form="request-acr-form" disabled={isSubmitting}>
                    {status === "error" ? "Try again" : "Send request"}
                  </Button>
                </ButtonGroupItem>
                {status === "error" ? (
                  <ButtonGroupItem>
                    <a className="usa-button usa-button--outline" href={mailtoHref}>Email it yourself</a>
                  </ButtonGroupItem>
                ) : null}
                <ButtonGroupItem>
                  <Button type="button" outline onClick={requestCancel} disabled={isSubmitting}>Cancel</Button>
                </ButtonGroupItem>
              </>
            )}
          </ButtonGroup>
        </ModalFooter>
      ) : null}
    </Modal>
  );
}

RequestAcrModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
